import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { extractErrorMessage } from '../api';
import { BetaReaderShell } from '../components/BetaReaderShell';
import { BetaStoryList } from '../components/BetaStoryList';
import { IconFeeds, IconRss, IconSliders } from '../components/icons';
import { ReaderFeedChips } from '../components/ReaderFeedChips';
import { SearchWindowToggle } from '../components/SearchWindowToggle';
import { SortToggle } from '../components/SortToggle';
import { SponsoredPlacementCard } from '../components/SponsoredPlacementCard';
import { V2SearchBar } from '../components/V2SearchBar';
import { useSortParam } from '../hooks/useSortParam';
import { useSponsoredPlacements } from '../hooks/useSponsoredPlacements';
import { useReaderRoutes } from '../providers/ReaderRoutesProvider';
import { useAdminMode, useUser } from '../providers/UserProvider';
import { listBetaSearches, setSearchIncluded, type BetaSearch } from '../repositories/betaSearchRepository';
import { streamForYouV2 } from '../repositories/feedsV2Repository';
import { loadPublicForYouV2, signedOutInterests } from '../repositories/signedOutV2Repository';
import { setAnonymousInterestsCookie } from '../utils/anonymousInterestsCookie';
import { formatAge } from '../utils/formatTime';
import {
  buildProgress, failedFeedsShownFromDelivery, INITIAL_FOR_YOU_V2, STORIES_PER_PAGE, type ForYouV2State,
} from '../utils/forYouV2';
import { sortV2Stories } from '../utils/feedsV2Sort';
import { windowFromParam, WINDOW_OPTIONS, type WindowHours } from '../utils/feedWindow';
import { forYouMix } from '../utils/rankAudit';
import { forYouV2ExportLinks, searchPagePath } from '../utils/readerRoutes';
import { handOffSearchAttempt, readerEvent, startSearchAttempt } from '../utils/readerTelemetry';

/**
 * For You on the feeds v2 engine (`/`, signed in): every feed the reader shows in For
 * You (the same feeds and switches as the current reader) built at once, as
 * each feed's own page builds it, and combined by the server (see
 * `utils/forYouV2`). Signed out, the same page shows the visitor's public feeds'
 * stored builds instead (`utils/signedOutV2`): a build spends decision-model
 * calls and takes an account, and the public feed builder keeps those fresh.
 */
export default function ForYouV2Page() {
  const navigate = useNavigate();
  const routes = useReaderRoutes();
  const [params, setParams] = useSearchParams();
  const hours: WindowHours = windowFromParam(params.get('hours'));
  const { isAdmin, isLoggedIn } = useUser();
  const { adminMode } = useAdminMode();
  const [interests, setInterests] = useState<BetaSearch[] | null>(null);
  const [state, setState] = useState<ForYouV2State>(INITIAL_FOR_YOU_V2);
  const [runKey, setRunKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sort, setSort] = useSortParam();
  // Phones hide the controls row behind the search bar's filters button, as Home did.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const sponsoredPlacements = useSponsoredPlacements();
  // Signed out: when the oldest of the stored builds shown was made.
  const [builtAt, setBuiltAt] = useState<string | null>(null);
  // How many stories are rendered; scrolling to the end renders the next page.
  const [pageCount, setPageCount] = useState(STORIES_PER_PAGE);
  const moreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let disposed = false;
    (isLoggedIn ? listBetaSearches().then(data => data.searches) : signedOutInterests())
      .then(searches => { if (!disposed) setInterests(searches); })
      .catch(err => { if (!disposed) setError(extractErrorMessage(err)); });
    return () => { disposed = true; };
  }, [isLoggedIn]);

  useEffect(() => {
    setState(INITIAL_FOR_YOU_V2);
    setPageCount(STORIES_PER_PAGE);
    const controller = new AbortController();
    const loading = isLoggedIn
      ? streamForYouV2({ hours, signal: controller.signal, onUpdate: setState })
      : loadPublicForYouV2({ hours }).then(next => { if (!controller.signal.aborted) { setState(next); setBuiltAt(next.builtAt); } });
    loading.catch((err) => {
      if (!controller.signal.aborted) setState((prev) => ({ ...prev, done: true, error: extractErrorMessage(err) }));
    });
    return () => controller.abort();
  }, [hours, runKey, isLoggedIn]);

  useEffect(() => { document.title = 'For You · TLDR Reader'; }, []);

  // Taking a feed out of For You builds For You again without it.
  const remove = async (feed: BetaSearch) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const updated = (interests ?? []).map(item => item.id === feed.id ? { ...item, included: false } : item);
      // Signed out, the visitor's switches live in a cookie, carried into the account at signup.
      if (isLoggedIn) await setSearchIncluded(feed.id, false);
      else setAnonymousInterestsCookie(updated);
      setInterests(updated);
      readerEvent('feed_home_toggle', { feed_id: feed.id, on: false, method: 'chip_x', feeds_v2: true });
      setRunKey(key => key + 1);
    } catch (err) { setError(`Couldn’t update “${feed.name}”: ${extractErrorMessage(err)}`); }
    finally { setBusy(false); }
  };

  const selectWindow = (next: WindowHours) => setParams(prev => {
    const updated = new URLSearchParams(prev);
    if (next === 72) updated.set('hours', '72'); else updated.delete('hours');
    return updated;
  }, { replace: true });

  // A search from For You starts its attempt here and hands it to the search page.
  const search = (query: string, method: 'search_box' | 'recent_search') => {
    const attempt = startSearchAttempt(method, query, hours);
    navigate(searchPagePath(query, hours === 72), { state: handOffSearchAttempt(attempt) });
  };

  const listKey = `for-you-v2:${hours}:${runKey}:${sort}:${isLoggedIn}`;
  const stories = useMemo(() => sortV2Stories(state.stories, sort), [state.stories, sort]);
  // The mix placed only this load's stories; delivered ones still shown are an earlier load's.
  const mix = useMemo(() => forYouMix(state.stories.slice(0, state.stories.length - state.fromDelivered)),
    [state.stories, state.fromDelivered]);
  // Delivered stories are an earlier load's; only this load's are reported as served.
  const unserved = useMemo(() => new Set(state.stories.slice(state.stories.length - state.fromDelivered).map(s => s.id)),
    [state.stories, state.fromDelivered]);
  const rendered = useMemo(() => stories.slice(0, pageCount), [stories, pageCount]);
  const hasMore = stories.length > rendered.length;
  useEffect(() => {
    const sentinel = moreRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) setPageCount(count => count + STORIES_PER_PAGE);
    }, { rootMargin: '800px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, rendered.length]);
  // Keep feed options together; phones reveal this toolbar with the filters button.
  const sortControl = <SortToggle selected={sort} onSelect={setSort} />;
  const included = (interests ?? []).filter(feed => feed.included);
  const exports = forYouV2ExportLinks(included.map(feed => feed.id), hours);
  const progress = state.done ? null : buildProgress(state.feeds);
  const failed = (state.feeds ?? []).filter(feed => feed.status === 'failed');
  const deliveredAge = state.delivered && state.fromDelivered > 0 ? formatAge(state.delivered.at) : null;
  const failedShown = failedFeedsShownFromDelivery(state);
  const failedFromDelivery = failed.filter(feed => failedShown.has(feed.search_id));
  const failedMissing = failed.filter(feed => !failedShown.has(feed.search_id));
  return (
    <BetaReaderShell exports={exports} feedSwitcher storyHotkeys
      search={<V2SearchBar onSearch={search}>
        <Link className="beta-filter-toggle" to={routes.feeds} aria-label={`Feeds: ${included.length} ${included.length === 1 ? 'feed' : 'feeds'} in Home`}>
          <IconFeeds />
          {included.length}
        </Link>
        <button className="beta-filter-toggle" type="button" aria-label="Feed options" aria-expanded={filtersOpen}
          aria-controls="beta-home-controls" onClick={() => setFiltersOpen(open => !open)}><IconSliders /></button>
      </V2SearchBar>}>
      {!isLoggedIn && builtAt && <p className="beta-message" role="status">Updated {formatAge(builtAt)}.</p>}
      <div id="beta-home-controls" className={`beta-home-controls${filtersOpen ? ' open' : ''}`}>
        {interests && <ReaderFeedChips interests={interests} busy={busy} onToggle={feed => void remove(feed)} />}
        <div className="beta-search-options-bar reader-for-you-options" role="toolbar" aria-label="Feed options">
          <SearchWindowToggle label="Feed time range" options={WINDOW_OPTIONS} selected={hours} onSelect={selectWindow} />
          {sortControl}
          <a className="beta-export" href={exports.rss}><IconRss />RSS</a><a className="beta-export reader-mono" href={exports.markdown}>.md</a>
        </div>
      </div>
      {(error || state.error) && (
        <div className="beta-error" role="alert">
          {error || state.error} <button type="button" onClick={() => { setError(''); setRunKey(key => key + 1); }}>Retry</button>
        </div>
      )}
      {failed.length > 0 && (
        <p className="beta-error" role="status">
          {failedFromDelivery.length > 0 && `Couldn’t refresh ${failedFromDelivery.map(feed => feed.name).join(', ')}; showing ${failedFromDelivery.length === 1 ? 'its' : 'their'} stories from ${deliveredAge}. `}
          {failedMissing.length > 0 && `Couldn’t refresh ${failedMissing.map(feed => feed.name).join(', ')}; ${failedMissing.length === 1 ? 'its' : 'their'} stories are missing.`}
          {isAdmin && adminMode && failed.map(feed => <span key={feed.search_id} className="fts-small"> {feed.name}: {feed.detail}</span>)}
        </p>
      )}
      {state.feeds?.length === 0 ? (
        <div className="beta-empty"><h1>Your own version of TLDR.</h1><p>Add the topics you care about to build your personal news feed.</p><Link className="beta-pill selected" to={routes.feeds}>Add news +</Link></div>
      ) : (
        <div className="beta-feed-container">
          {sponsoredPlacements.map((ad, index) => (
            <SponsoredPlacementCard key={ad.id} placement={ad} autoFocus={index === 0} initiallyExpanded={index === 0} />
          ))}
          <BetaStoryList eventContext={{ surface: isLoggedIn ? 'for_you' : 'default', surfaceId: null, properties: { feeds_v2: true } }}
            articles={rendered} unservedIds={unserved} showInterests resetKey={listKey}
            autoFocusFirst={sponsoredPlacements.length === 0} isAdmin={isAdmin && adminMode} mix={mix}
            settled={state.done} />
          {hasMore && <div ref={moreRef} className="beta-message" role="status">Loading more stories…</div>}
        </div>
      )}
      {progress && (
        <p className="beta-message" role="status">
          <span className="spinner" style={{ marginRight: 8 }} />
          {deliveredAge ? `Showing your For You from ${deliveredAge} · ${progress}`
            : state.stories.length > 0 && state.provisional ? `Showing saved stories · ${progress}` : progress}
        </p>
      )}
      {state.done && !state.error && state.feeds && state.feeds.length > 0 && stories.length === 0 && (
        <p className="beta-message beta-message-centered">No stories in the last {hours} hours.</p>
      )}
    </BetaReaderShell>
  );
}
